import { shapePresets } from "./presets.js";
import {
  powerPointLineTools,
  powerPointModernGeometricShapeTypes,
  powerPointPresetShapeTypes,
  powerPointShapeCatalog,
  type PowerPointLineTool,
  type PowerPointShapeGroup,
} from "./powerpoint-shape-catalog.js";
import { canCreatePowerPointShapePath } from "./shape-paths.js";
import type { PowerPointShapeTarget, ShapeKind, ShapePreset } from "./types.js";

export type PowerPointShapeRegistryStatus =
  | "editable"
  | "planned"
  | "legacy-planned";

export type PowerPointShapeRegistryTarget =
  | {
      readonly kind: "lineTool";
      readonly id: string;
      readonly connectorType?: PowerPointLineTool["connectorType"];
      readonly arrowheads: PowerPointLineTool["arrowheads"];
      readonly freeformTool?: PowerPointLineTool["freeformTool"];
    }
  | {
      readonly kind: "geometricShape";
      readonly shapeType: string;
    }
  | {
      readonly kind: "legacyShape";
      readonly shapeType: string;
    };

export interface PowerPointShapeRegistryEntry {
  readonly targetKey: string;
  readonly label: string;
  readonly group: PowerPointShapeGroup;
  readonly target: PowerPointShapeRegistryTarget;
  readonly status: PowerPointShapeRegistryStatus;
  readonly pathReady: boolean;
  readonly editablePresetId?: ShapeKind;
}

function targetKey(target: PowerPointShapeTarget): string {
  return `${target.kind}:${target.id}`;
}

function registryTargetKey(target: PowerPointShapeRegistryTarget): string {
  switch (target.kind) {
    case "lineTool":
      return `lineTool:${target.id}`;
    case "geometricShape":
      return `geometricShape:${target.shapeType}`;
    case "legacyShape":
      return `legacyShape:${target.shapeType}`;
  }
}

function shapeTypeLabel(shapeType: string): string {
  return shapeType
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^Flow Chart /, "Flowchart ")
    .replace(/^Action Button /, "Action Button ")
    .replace(/\s+/g, " ")
    .trim();
}

const editablePresetByTargetKey = new Map<string, ShapePreset>(
  shapePresets.map((preset) => [targetKey(preset.powerPointTarget), preset]),
);

function editablePresetFor(
  target: PowerPointShapeRegistryTarget,
): ShapePreset | undefined {
  if (target.kind === "lineTool") {
    return editablePresetByTargetKey.get(`lineTool:${target.id}`);
  }
  if (target.kind === "geometricShape") {
    return editablePresetByTargetKey.get(`geometricShape:${target.shapeType}`);
  }
  return undefined;
}

const lineEntries: PowerPointShapeRegistryEntry[] = powerPointLineTools.map(
  (tool) => {
    const target: PowerPointShapeRegistryTarget = {
      kind: "lineTool",
      id: tool.id,
      connectorType: tool.connectorType,
      arrowheads: tool.arrowheads,
      freeformTool: tool.freeformTool,
    };
    const preset = editablePresetFor(target);
    return {
      targetKey: registryTargetKey(target),
      label: tool.label,
      group: "lines",
      target,
      status: preset ? "editable" : "planned",
      pathReady: false,
      editablePresetId: preset?.id,
    };
  },
);

const geometricEntries: PowerPointShapeRegistryEntry[] =
  powerPointShapeCatalog.flatMap((group) =>
    group.shapeTypes.map((shapeType) => {
      const target: PowerPointShapeRegistryTarget =
        group.group === "legacy"
          ? { kind: "legacyShape", shapeType }
          : { kind: "geometricShape", shapeType };
      const preset = editablePresetFor(target);
      const pathReady =
        target.kind === "geometricShape" &&
        canCreatePowerPointShapePath(target.shapeType);
      return {
        targetKey: registryTargetKey(target),
        label: shapeTypeLabel(shapeType),
        group: group.group,
        target,
        status: preset
          ? "editable"
          : group.group === "legacy"
            ? "legacy-planned"
            : "planned",
        pathReady,
        editablePresetId: preset?.id,
      };
    }),
  );

export const powerPointShapeRegistry: readonly PowerPointShapeRegistryEntry[] =
  [...lineEntries, ...geometricEntries];

export const editablePowerPointShapeRegistry = powerPointShapeRegistry.filter(
  (entry) => entry.status === "editable",
);

export const powerPointShapeRegistryByTargetKey = new Map(
  powerPointShapeRegistry.map((entry) => [entry.targetKey, entry]),
);

export const powerPointShapeRegistryStats = {
  lineTools: powerPointLineTools.length,
  modernGeometricPresets: powerPointModernGeometricShapeTypes.length,
  legacyGeometricPresets:
    powerPointPresetShapeTypes.length -
    powerPointModernGeometricShapeTypes.length,
  totalTargets: powerPointShapeRegistry.length,
  editableTargets: editablePowerPointShapeRegistry.length,
  plannedTargets: powerPointShapeRegistry.filter(
    (entry) => entry.status === "planned",
  ).length,
  legacyPlannedTargets: powerPointShapeRegistry.filter(
    (entry) => entry.status === "legacy-planned",
  ).length,
  pathReadyTargets: powerPointShapeRegistry.filter((entry) => entry.pathReady)
    .length,
} as const;
